import { useMemo } from 'react'

import type { ToolbarFileListItem } from '#/shared/api/generated/shipment/models'
import { useListItemsApiV1ToolbarTypeGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { formatNoteTimestamp } from '#/shared/lib/format'

const DEFAULT_FILE_FEED_SIZE = 100

export interface ShipmentFileFeedItem {
  id: string
  name: string
  url: string
  mimeType: string | undefined
  sizeBytes: number | undefined
  thumbnailUrl: string | undefined
  tag: string | undefined
  isOrderFile: boolean
  authorName: string | undefined
  createdAt: string
  timestamp: string
}

interface UseShipmentFileFeedOptions {
  enabled?: boolean
  size?: number
  isOrderFile?: boolean
}

export interface UseShipmentFileFeedResult {
  files: ShipmentFileFeedItem[]
  isLoading: boolean
  isFetching: boolean
}

export function toShipmentFileFeedItem(
  file: ToolbarFileListItem,
): ShipmentFileFeedItem {
  const fullName = [file.user?.first_name, file.user?.last_name]
    .filter(Boolean)
    .join(' ')
  return {
    id: file.id,
    name: file.name,
    url: '',
    mimeType: file.mime_type ?? undefined,
    sizeBytes: file.size_bytes ?? undefined,
    thumbnailUrl: file.thumbnail_url ?? undefined,
    tag: file.tag ?? undefined,
    isOrderFile: file.is_order_file ?? false,
    authorName: fullName || undefined,
    createdAt: file.created_at,
    timestamp: file.created_at ? formatNoteTimestamp(file.created_at) : '',
  }
}

export function useShipmentFileFeed(
  shipmentId: string,
  {
    enabled = true,
    size = DEFAULT_FILE_FEED_SIZE,
    isOrderFile,
  }: UseShipmentFileFeedOptions = {},
): UseShipmentFileFeedResult {
  const query = useListItemsApiV1ToolbarTypeGet(
    'file',
    {
      shipment_id: shipmentId,
      size,
      ...(isOrderFile !== undefined ? { file_is_order_file: isOrderFile } : {}),
    },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  const files = useMemo<ShipmentFileFeedItem[]>(() => {
    const items = (query.data?.data.items ??
      []) as unknown as ToolbarFileListItem[]
    return items.map(toShipmentFileFeedItem)
  }, [query.data])

  const armed = enabled && Boolean(shipmentId)
  return {
    files,
    isLoading: query.isLoading && armed,
    isFetching: query.isFetching && armed,
  }
}
